import { ClipboardCheck, ClipboardList, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	useDirecionadaMetrics,
	useQuizQuestionsDrilldown,
} from '@/api/queries/kpi-metrics-queries'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { KpiMetricsParams } from '@/types'
import { AccordionBarChart } from './shared/accordion-bar-chart'
import { BarListRow } from './shared/bar-list-row'
import { KpiMetricsDashboardLoading } from './shared/kpi-metrics-dashboard-loading'
import { KpiStatCard } from './shared/kpi-stat-card'

const ANSWER_COLOR: Record<string, string> = {
	COMPLIANT: 'bg-kaefer-grass-green',
	NON_COMPLIANT: 'bg-kaefer-red',
	NOT_APPLICABLE: 'bg-kaefer-medium-grey',
}

type AnswerBreakdown = {
	compliant: number
	nonCompliant: number
	notApplicable: number
}

function AnswerStackedBar({
	breakdown,
	max,
}: {
	breakdown: AnswerBreakdown
	max: number
}) {
	const { compliant, nonCompliant, notApplicable } = breakdown
	return (
		<div className="flex h-3 overflow-hidden bg-kaefer-light-silver/60">
			{compliant > 0 && (
				<div
					className={`h-full ${ANSWER_COLOR.COMPLIANT}`}
					style={{ width: `${(compliant / max) * 100}%` }}
				/>
			)}
			{nonCompliant > 0 && (
				<div
					className={`h-full ${ANSWER_COLOR.NON_COMPLIANT}`}
					style={{ width: `${(nonCompliant / max) * 100}%` }}
				/>
			)}
			{notApplicable > 0 && (
				<div
					className={`h-full ${ANSWER_COLOR.NOT_APPLICABLE}`}
					style={{ width: `${(notApplicable / max) * 100}%` }}
				/>
			)}
		</div>
	)
}

function AnswerLegend({ t }: { t: (key: string) => string }) {
	return (
		<div className="mt-3 flex gap-4 text-[11px] text-muted-foreground">
			<span className="flex items-center gap-1.5">
				<span className={`size-2.5 rounded-[2px] ${ANSWER_COLOR.COMPLIANT}`} />
				{t('kpiMetrics.direcionada.compliant')}
			</span>
			<span className="flex items-center gap-1.5">
				<span
					className={`size-2.5 rounded-[2px] ${ANSWER_COLOR.NON_COMPLIANT}`}
				/>
				{t('kpiMetrics.direcionada.nonCompliant')}
			</span>
			<span className="flex items-center gap-1.5">
				<span
					className={`size-2.5 rounded-[2px] ${ANSWER_COLOR.NOT_APPLICABLE}`}
				/>
				{t('kpiMetrics.direcionada.notApplicable')}
			</span>
		</div>
	)
}

function AnswerBreakdownTooltip({
	breakdown,
	t,
}: {
	breakdown: AnswerBreakdown
	t: (key: string) => string
}) {
	const total =
		breakdown.compliant + breakdown.nonCompliant + breakdown.notApplicable
	const entries = [
		{
			key: 'COMPLIANT',
			label: t('kpiMetrics.direcionada.compliant'),
			value: breakdown.compliant,
		},
		{
			key: 'NON_COMPLIANT',
			label: t('kpiMetrics.direcionada.nonCompliant'),
			value: breakdown.nonCompliant,
		},
		{
			key: 'NOT_APPLICABLE',
			label: t('kpiMetrics.direcionada.notApplicable'),
			value: breakdown.notApplicable,
		},
	]

	return (
		<div className="flex flex-col gap-1">
			{entries.map((entry) => (
				<div
					key={entry.key}
					className="grid grid-cols-[auto_1fr_auto] items-center gap-1.5"
				>
					<span
						className={cn(
							'size-2 shrink-0 rounded-[2px]',
							ANSWER_COLOR[entry.key],
						)}
					/>
					<span className="truncate text-left">{entry.label}</span>
					<span className="text-muted-foreground tabular-nums">
						{entry.value} (
						{total > 0 ? Math.round((entry.value / total) * 100) : 0}%)
					</span>
				</div>
			))}
		</div>
	)
}

function QuizQuestionsDrilldown({
	quizId,
	params,
	t,
}: {
	quizId: string
	params?: KpiMetricsParams
	t: (key: string) => string
}) {
	const { data: questions, isLoading } = useQuizQuestionsDrilldown(
		quizId,
		params,
	)

	if (isLoading) {
		return (
			<p className="py-3 text-center text-xs text-muted-foreground">
				{t('kpiMetrics.charts.loading')}
			</p>
		)
	}

	if (!questions || questions.length === 0) {
		return (
			<p className="py-3 text-center text-xs text-muted-foreground">
				{t('kpiMetrics.charts.noData')}
			</p>
		)
	}

	const maxQuestion = Math.max(1, ...questions.map((q) => q.count))

	return (
		<div className="flex flex-col gap-2.5 border-t border-kaefer-light-silver pt-2.5">
			{questions.map((question) => (
				<Tooltip key={question.questionId}>
					<TooltipTrigger asChild>
						<div className="flex flex-col gap-1">
							<div className="flex items-center justify-between text-xs">
								<span className="truncate text-kaefer-dark-grey">
									{question.questionText}
								</span>
								<span className="tabular-nums text-muted-foreground">
									{question.count}
								</span>
							</div>
							<AnswerStackedBar breakdown={question} max={maxQuestion} />
						</div>
					</TooltipTrigger>
					<TooltipContent side="top" align="center" className="max-w-64">
						<AnswerBreakdownTooltip breakdown={question} t={t} />
					</TooltipContent>
				</Tooltip>
			))}
		</div>
	)
}

export function DirecionadaDashboard({
	params,
}: {
	params?: KpiMetricsParams
}) {
	const { t } = useTranslation()
	const { data, isLoading } = useDirecionadaMetrics(params)
	const [expandedQuiz, setExpandedQuiz] = useState<string[]>([])

	if (isLoading || !data) return <KpiMetricsDashboardLoading />

	const {
		kpis,
		pending: direcionadaPending,
		answerStats: direcionadaAnswerStats,
		auditorRanking: direcionadaAuditorRanking,
		quizDistribution,
	} = data

	const sortedQuizzes = [...quizDistribution.byQuiz].sort(
		(a, b) => b.count - a.count,
	)
	const maxQuiz = Math.max(1, ...sortedQuizzes.map((q) => q.count))
	const maxAuditor = Math.max(
		1,
		...direcionadaAuditorRanking.map((a) => a.count),
	)
	const maxBranchPending = Math.max(
		1,
		...direcionadaPending.byBranch.map((b) => b.count),
	)
	const pendingPct =
		kpis.total > 0
			? Math.round((direcionadaPending.total / kpis.total) * 100)
			: 0
	const questionnaires = kpis.questionnaires
	const questionnairesCompliantPct =
		questionnaires.total > 0
			? Math.round((questionnaires.compliant / questionnaires.total) * 100)
			: 0
	const questionnairesNonCompliantPct =
		questionnaires.total > 0
			? Math.round((questionnaires.nonCompliant / questionnaires.total) * 100)
			: 0

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<QuestionnairesKpiStatCard
					total={questionnaires.total}
					compliant={questionnaires.compliant}
					nonCompliant={questionnaires.nonCompliant}
					compliantPct={questionnairesCompliantPct}
					nonCompliantPct={questionnairesNonCompliantPct}
					t={t}
				/>
				<AnswersKpiStatCard breakdown={direcionadaAnswerStats} t={t} />
				<KpiStatCard
					icon={<ClipboardCheck className="size-4" />}
					iconClass="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue"
					label={t('kpiMetrics.direcionada.resolutionRate')}
					value={`${kpis.compliancePercentage}%`}
				/>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="h-full">
							<KpiStatCard
								icon={<Send className="size-4" />}
								iconClass="bg-kaefer-honey-yellow/20 text-kaefer-dark-grey"
								label={t('kpiMetrics.direcionada.pending')}
								value={String(direcionadaPending.total)}
								sub={`${pendingPct}% ${t('kpiMetrics.direcionada.ofTotal')}`}
							/>
						</div>
					</TooltipTrigger>
					<TooltipContent side="top" align="center">
						{direcionadaPending.total} / {kpis.total} ({pendingPct}%)
					</TooltipContent>
				</Tooltip>
			</div>

			<div className="grid grid-cols-1 gap-4">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.direcionada.quizDistribution')}
					</span>
					<div className="mt-1 max-h-80 overflow-y-auto pr-1">
						<AccordionBarChart
							items={sortedQuizzes.map((quiz) => ({
								id: quiz.quizId,
								label: quiz.quizTitle,
								value: quiz.count,
								tooltip: <AnswerBreakdownTooltip breakdown={quiz} t={t} />,
							}))}
							getChildId={() => ''}
							getChildLabel={() => ''}
							getChildValue={() => 0}
							renderRow={(item) => {
								const quiz = sortedQuizzes.find((q) => q.quizId === item.id)
								if (!quiz) return null
								return (
									<div className="flex flex-col gap-1">
										<div className="flex items-center justify-between text-xs">
											<span className="truncate text-kaefer-dark-grey">
												{quiz.quizTitle}
											</span>
											<span className="tabular-nums text-muted-foreground">
												{quiz.count}
											</span>
										</div>
										<AnswerStackedBar breakdown={quiz} max={maxQuiz} />
									</div>
								)
							}}
							renderChildren={(item) => (
								<QuizQuestionsDrilldown
									quizId={item.id}
									params={params}
									t={t}
								/>
							)}
							expanded={expandedQuiz}
							onExpandedChange={setExpandedQuiz}
							emptyState={<EmptyState t={t} />}
						/>
					</div>
					<AnswerLegend t={t} />
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.direcionada.auditorRanking')}
					</span>
					{direcionadaAuditorRanking.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3 flex max-h-80 flex-col gap-2.5 overflow-y-auto pr-1">
							{direcionadaAuditorRanking.map((a) => (
								<BarListRow
									key={a.personId}
									label={a.personName}
									displayValue={String(a.count)}
									widthPct={(a.count / maxAuditor) * 100}
									colorClass="bg-kaefer-violet"
								/>
							))}
						</div>
					)}
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('kpiMetrics.direcionada.pendingByBranch')}
					</span>
					{direcionadaPending.byBranch.length === 0 ? (
						<EmptyState t={t} />
					) : (
						<div className="mt-3 flex max-h-80 flex-col gap-2.5 overflow-y-auto pr-1">
							{direcionadaPending.byBranch.map((b) => (
								<BarListRow
									key={b.branchId}
									label={b.branchName}
									displayValue={String(b.count)}
									widthPct={(b.count / maxBranchPending) * 100}
									colorClass="bg-kaefer-red"
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

function AnswersKpiStatCard({
	breakdown,
	t,
}: {
	breakdown: { total: number; byAnswer: Record<string, number> }
	t: (key: string) => string
}) {
	const total = breakdown.total
	const compliant = breakdown.byAnswer.COMPLIANT ?? 0
	const nonCompliant = breakdown.byAnswer.NON_COMPLIANT ?? 0
	const notApplicable = breakdown.byAnswer.NOT_APPLICABLE ?? 0
	const pct = (value: number) =>
		total > 0 ? Math.round((value / total) * 100) : 0

	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div className="flex size-8 items-center justify-center rounded-md bg-kaefer-violet/15 text-kaefer-violet">
				<ClipboardList className="size-4" />
			</div>
			<p className="mt-2 text-2xl font-bold text-kaefer-dark-grey">{total}</p>
			<p className="text-xs font-medium text-kaefer-dark-grey">
				{t('kpiMetrics.direcionada.answers')}
			</p>

			<div className="mt-2 flex flex-col gap-1 border-kaefer-light-silver border-t pt-2">
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{t('kpiMetrics.direcionada.compliant')}
					</span>
					<span className="text-xs font-bold text-kaefer-forest-green tabular-nums">
						{compliant} ({pct(compliant)}%)
					</span>
				</div>
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{t('kpiMetrics.direcionada.nonCompliant')}
					</span>
					<span className="text-xs font-bold text-kaefer-red tabular-nums">
						{nonCompliant} ({pct(nonCompliant)}%)
					</span>
				</div>
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{t('kpiMetrics.direcionada.notApplicable')}
					</span>
					<span className="text-xs font-bold text-kaefer-medium-grey tabular-nums">
						{notApplicable} ({pct(notApplicable)}%)
					</span>
				</div>
			</div>
		</div>
	)
}

function QuestionnairesKpiStatCard({
	total,
	compliant,
	nonCompliant,
	compliantPct,
	nonCompliantPct,
	t,
}: {
	total: number
	compliant: number
	nonCompliant: number
	compliantPct: number
	nonCompliantPct: number
	t: (key: string) => string
}) {
	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div className="flex size-8 items-center justify-center rounded-md bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue">
				<ClipboardList className="size-4" />
			</div>
			<p className="mt-2 text-2xl font-bold text-kaefer-dark-grey">{total}</p>
			<p className="text-xs font-medium text-kaefer-dark-grey">
				{t('kpiMetrics.direcionada.questionnaires')}
			</p>

			<div className="mt-2 flex flex-col gap-1 border-kaefer-light-silver border-t pt-2">
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{t('kpiMetrics.direcionada.questionnairesCompliant')}
					</span>
					<span className="text-xs font-bold text-kaefer-forest-green tabular-nums">
						{compliant} ({compliantPct}%)
					</span>
				</div>
				<div className="flex items-center justify-between gap-2">
					<span className="text-[11px] text-muted-foreground">
						{t('kpiMetrics.direcionada.questionnairesNonCompliant')}
					</span>
					<span className="text-xs font-bold text-kaefer-red tabular-nums">
						{nonCompliant} ({nonCompliantPct}%)
					</span>
				</div>
			</div>
		</div>
	)
}

function EmptyState({ t }: { t: (key: string) => string }) {
	return (
		<p className="py-10 text-center text-sm text-muted-foreground">
			{t('kpiMetrics.charts.noData')}
		</p>
	)
}
